import React from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export type ModalStateProps = {
  show: boolean;
  onHide: () => void;
};

export type ModalSize = 'sm' | 'md' | 'lg';

export type ModalProps = ModalStateProps & {
  readonly children?: React.ReactNode;
  readonly closeOnOutsideClick?: boolean;
  readonly title?: React.ReactNode;
  readonly footer?: React.ReactNode;
  readonly actions?: React.ReactNode;
  readonly noBody?: boolean;
  readonly showCloseButton?: boolean;
  readonly size?: ModalSize;
  readonly scrollable?: boolean;
  readonly width?: string | number;
  readonly 'data-cy'?: string;
};

function ModalHeader({
  children,
  ...rest
}: {
  children?: React.ReactNode;
  [key: string]: unknown;
}) {
  return <DialogTitle {...rest}>{children}</DialogTitle>;
}

function ModalBody({ children, ...rest }: { children?: React.ReactNode; [key: string]: unknown }) {
  return <DialogContent {...rest}>{children}</DialogContent>;
}

function ModalFooter({
  children,
  ...rest
}: {
  children?: React.ReactNode;
  [key: string]: unknown;
}) {
  return <DialogActions {...rest}>{children}</DialogActions>;
}

type IModal = React.FC<ModalProps> & {
  Header: typeof ModalHeader;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
};

const SIZE_TO_MAX_WIDTH: Record<ModalSize, 'sm' | 'md' | 'lg'> = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
};

/**
 * Modal dialog built on MUI Dialog. Supports optional title, actions in header,
 * close button, footer, and compound components (Modal.Body, Modal.Footer) for form layouts.
 */
export const Modal: IModal = (props) => {
  const {
    title,
    footer,
    onHide,
    children,
    closeOnOutsideClick = false,
    noBody,
    showCloseButton,
    actions,
    size = 'md',
    scrollable,
    width,
    'data-cy': dataCy,
  } = props;

  const handleClose = (_: object, reason: string) => {
    if (!closeOnOutsideClick && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }

    onHide();
  };

  const maxWidth = width ? false : SIZE_TO_MAX_WIDTH[size];
  const paperSx = width ? { maxWidth: width } : undefined;

  return (
    <Dialog
      open={props.show}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={!width}
      scroll={scrollable ? 'paper' : undefined}
      PaperProps={paperSx ? { sx: paperSx } : undefined}
      data-cy={dataCy}
    >
      {title && (
        <DialogTitle
          component="div"
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%',
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>{title}</Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {actions}
            {showCloseButton !== false && (
              <IconButton size="small" onClick={onHide} sx={{ ml: 0.5 }} aria-label="close">
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
      )}
      {noBody ? children : <DialogContent>{children}</DialogContent>}
      {footer && <DialogActions sx={{ px: 2, py: 1.5 }}>{footer}</DialogActions>}
    </Dialog>
  );
};

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
